import { useEffect, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Radio, Select, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  EvaluationScenario,
  ProtocolApplicability,
  ScenarioCheck,
  ScenarioDecisionRef,
  ScenarioVersionCheck,
  ScenarioVersionRef,
  WorldId,
} from '@/api/scenarioVersions';
import { listEvaluationWorlds, type EvaluationWorldEntry } from '@/api/scenarioVersions';
import { StatusPill } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import {
  APPLICABILITY_WORDS,
  KIND_WORDS,
  ORIGIN_WORDS,
  checkEvidence,
  checkName,
  checkSentence,
  isAlternatives,
  shortHash,
  strengthWords,
} from '../scenarioWords';
import { ReviewRecord, cardStyle, surface } from '../review/reviewParts';
import {
  CHECK_TYPES,
  addCheck,
  addTurn,
  checksAtTurn,
  heldAtTurn,
  removeCheck,
  removeTurn,
  setApplicability,
  setTurnText,
  updateArgs,
  updateCheck,
} from './scenarioEdits';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The advanced editor's sections (screen 10): Context, Actors & timeline, Expectations, Checks & evidence and
// Provenance here; version 3's hazard and sources, tool answers, controls and siblings in caseBasisSections — readable
// words, no JSON, no operator names. Each section edits the draft through scenarioEdits; the
// server decides what compiles and says why in words.
// ─────────────────────────────────────────────────────────────────────────────

export interface SectionProps {
  draft: EvaluationScenario;
  original: EvaluationScenario;
  onChange: (next: EvaluationScenario) => void;
  check: ScenarioVersionCheck | null;
  editable: boolean;
  isDark: boolean;
}

export const sectionTitle = { fontSize: 16, fontWeight: 600, display: 'block', marginBottom: 2 } as const;
export const fieldLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', display: 'block', marginBottom: 4 } as const;
const tags = (values: string[] | undefined) => (values ?? []).filter((v) => v !== undefined);

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={fieldLabel}>{label}</Text>
      {children}
      {hint && <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 3 }}>{hint}</Text>}
    </div>
  );
}

// ── Context ─────────────────────────────────────────────────────────────────

export function ContextSection({
  draft, original, onChange, check, editable, isDark, agentName, onCreateVariant,
}: SectionProps & { agentName: string; onCreateVariant: () => void }) {
  const standalone = draft.world.infusionProtocolId <= 0;
  const wasLinked = original.world.infusionProtocolId > 0;
  const needs = draft.checks.filter((c) => c.applicability?.withoutProtocol === 'NeedsProtocol');
  const notApplicable = draft.checks.filter((c) => c.applicability?.withoutProtocol === 'NotApplicable');
  const undeclared = wasLinked && standalone ? draft.checks.filter((c) => !c.applicability) : [];
  const setWorld = (field: WorldId, value: number) => onChange({ ...draft, world: { ...draft.world, [field]: value } });
  const role = draft.world.role;
  const verdict: ProtocolApplicability | null = !standalone ? null : needs.length > 0 ? 'NeedsProtocol' : notApplicable.length > 0 ? 'NotApplicable' : 'Applies';
  // World seeder W5: the worlds this agent's therapy can be tested in. A run refuses any other.
  const [worlds, setWorlds] = useState<EvaluationWorldEntry[] | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    listEvaluationWorlds(ac.signal).then(setWorlds).catch(() => { if (!ac.signal.aborted) setWorlds([]); });
    return () => ac.abort();
  }, []);
  const forAgent = (worlds ?? []).filter((w) => w.agentIds.includes(draft.target.agentId));
  const named = role ? (worlds ?? []).find((w) => w.role === role) : undefined;
  const roleProblem = !role || worlds == null ? null
    : !named ? `“${role}” is not in the world catalog, so a run refuses this version.`
    : !named.agentIds.includes(draft.target.agentId)
      ? `“${named.words}” is set up on ${named.templateName ?? `template ${named.templateId}`}, which this agent does not serve, so a run refuses this version.`
      : null;

  return (
    <section id="section-context" aria-label="Context">
      <Text style={sectionTitle}>Context</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Who is tested, in what world, at what moment. Change only what this case is about.</Text>

      <Field label="Agent" hint="This scenario belongs to this agent. Copy it to test another.">
        <Text strong>{agentName}</Text> <Text type="secondary">· #{draft.target.agentId}</Text>
      </Field>
      <Field label="Where it runs">
        {draft.world.merchantId > 0 ? (
          <Text>Practice #{draft.world.merchantId}, pinned by this scenario. Results stay with the practice that ran it.</Text>
        ) : (
          <Text>At the practice chosen for each run. The scenario is shared by the platform; its results stay with the practice that ran it.</Text>
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <Field label="Synthetic patient" hint={draft.world.memberId > 0 ? 'The run reads this synthetic patient’s records, as production reads them.' : 'No patient is bound, and none is invented.'}>
          <InputNumber min={0} disabled={!editable} value={draft.world.memberId} onChange={(v) => setWorld('memberId', Number(v ?? 0))} aria-label="Synthetic patient id" style={{ width: '100%' }} />
        </Field>
        <Field label="Who speaks" hint={draft.world.memberContactId > 0 ? `Family contact #${draft.world.memberContactId}, signed in for the patient.` : 'The patient, signed in as themself.'}>
          <InputNumber min={0} disabled={!editable} value={draft.world.memberContactId} onChange={(v) => setWorld('memberContactId', Number(v ?? 0))} aria-label="Family contact id" style={{ width: '100%' }} />
        </Field>
      </div>
      <Field label="Named world · optional"
        hint="A point in a protocol that each run sets up for this case on a reserved patient of practice 100, such as FOLFOX, cycle 2, day 10. In a named world the case runs on the world’s patient and course, not on the patient above.">
        <Select allowClear disabled={!editable} value={role || undefined} aria-label="Named world" style={{ width: '100%' }}
          placeholder={worlds == null ? 'Loading worlds…' : forAgent.length > 0 ? 'No named world' : 'No world is set up for this agent’s therapy yet'}
          options={[
            ...forAgent.map((w) => ({ value: w.role, label: `${w.words} · ${w.role}` })),
            ...(role && !forAgent.some((w) => w.role === role) ? [{ value: role, label: named ? `${named.words} · ${role}` : role }] : []),
          ]}
          onChange={(v: string | undefined) => onChange({ ...draft, world: { ...draft.world, role: v || undefined } })} />
        {roleProblem && <Text type="danger" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{roleProblem}</Text>}
      </Field>

      <Field label="Protocol context · optional">
        <Radio.Group
          disabled={!editable}
          value={standalone ? 'none' : 'protocol'}
          onChange={(e) => setWorld('infusionProtocolId', e.target.value === 'none' ? 0 : original.world.infusionProtocolId || 1)}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <Radio value="none">No protocol: standalone</Radio>
          <Radio value="protocol">
            Protocol{' '}
            <InputNumber size="small" min={1} disabled={!editable || standalone} value={standalone ? undefined : draft.world.infusionProtocolId}
              onChange={(v) => setWorld('infusionProtocolId', Number(v ?? 1))} aria-label="Protocol id" />
          </Radio>
        </Radio.Group>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 10 }}>
          {(['Applies', 'NotApplicable', 'NeedsProtocol'] as ProtocolApplicability[]).map((a) => (
            <div key={a} style={{ ...cardStyle(isDark), padding: 10, borderColor: verdict === a ? colors.violet[500] : surface(isDark).line }}>
              <StatusPill tone={a === 'Applies' ? 'green' : a === 'NotApplicable' ? 'slate' : 'red'}>{APPLICABILITY_WORDS[a].label}</StatusPill>
              <div style={{ fontSize: 12.5, marginTop: 6 }}>{APPLICABILITY_WORDS[a].detail}</div>
            </div>
          ))}
        </div>
        {!standalone && <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>A protocol is bound, so every check runs.</Text>}
      </Field>

      {standalone && (wasLinked || draft.checks.some((c) => c.applicability)) && (
        <Field label="Without a protocol, each check…" hint="Not applicable needs a reviewed rule and its reason. Removing context never hides a check.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {draft.checks.map((c) => <ApplicabilityRow key={c.id} draft={draft} c={c} editable={editable} onChange={onChange} isDark={isDark} />)}
          </div>
        </Field>
      )}

      {undeclared.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title={`Say for ${undeclared.length} check(s) whether they still apply without a protocol`}
          description="The version cannot be saved until every check says it applies, needs protocol facts, or is Not applicable by a reviewed rule." />
      )}
      {needs.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          title={`Blocked: ${needs.length} check(s) need protocol facts a standalone context cannot supply`}
          description={
            <>
              <div>{`${needs.map((c) => checkName(draft, c)).join('; ')}. Without a protocol the world has no cycle, so this version would be refused before its first turn. It is not marked Not applicable: no reviewed rule says these checks stop applying without a protocol.`}</div>
              {wasLinked && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <Button size="small" disabled={!editable} onClick={() => setWorld('infusionProtocolId', original.world.infusionProtocolId)}>Keep the protocol</Button>
                  <Button size="small" onClick={onCreateVariant}>Create standalone variant…</Button>
                </div>
              )}
            </>
          }
        />
      )}

      <Field label="Evaluation scope" hint="Derived from the version: a bound protocol makes it protocol-linked, a peer consultation makes it a collaboration.">
        <Text>{({ Standalone: 'Standalone', Collaboration: 'Collaboration (consults a peer)', ProtocolLinked: 'Protocol-linked' } as Record<string, string>)[check?.evaluationScope ?? draft.evaluationScope ?? ''] ?? '—'}</Text>
      </Field>
      <Alert type="info" showIcon title={role ? 'A named world, set up for each run' : 'Synthetic world and clock: not in this version'}
        description={role
          ? `Each run sets up “${named?.words ?? role}” on a reserved patient of practice 100, runs this case there, and removes it afterwards. The world holds the course and its history before today; it does not hold doses, labs, vitals or a caregiver, so a check that needs one cannot rely on it.`
          : 'A run uses the chosen practice’s synthetic patient records, which the patient brief reads as production reads them. Name a world to run this case at a set point in a protocol.'} />
      <Field label="Execution mode">
        <Text>
          Isolated real-agent run. A tool that changes a record or sends something is answered by a double and reported as Not carried out.
          {(draft.toolDoubles ?? []).length > 0 && ' A tool this scenario answers for (Tool answers) returns what the scenario says, and never runs.'}
        </Text>
      </Field>
    </section>
  );
}

function ApplicabilityRow({ draft, c, editable, onChange, isDark }: { draft: EvaluationScenario; c: ScenarioCheck; editable: boolean; onChange: (s: EvaluationScenario) => void; isDark: boolean }) {
  const a = c.applicability;
  return (
    <div style={{ ...cardStyle(isDark), padding: 10 }}>
      <Text type="secondary" style={{ fontSize: 11.5 }}>{checkName(draft, c)}</Text>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{checkSentence(c)}</div>
      <Select<ProtocolApplicability>
        size="small"
        disabled={!editable}
        placeholder="Choose…"
        value={a?.withoutProtocol}
        onChange={(v) => onChange(setApplicability(draft, c.id, { withoutProtocol: v, reason: a?.reason, ruleRef: a?.ruleRef }))}
        aria-label={`Without a protocol, ${checkName(draft, c)}`}
        style={{ minWidth: 260 }}
        options={[
          { value: 'Applies', label: 'Runs: needs no protocol' },
          { value: 'NeedsProtocol', label: 'Needs protocol facts (blocks the version)' },
          { value: 'NotApplicable', label: 'Not applicable: a reviewed rule says so' },
        ]}
      />
      {a?.withoutProtocol === 'NotApplicable' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 6 }}>
          <Input size="small" disabled={!editable} placeholder="Why it does not apply" value={a.reason}
            onChange={(e) => onChange(setApplicability(draft, c.id, { ...a, reason: e.target.value }))} aria-label="Reason it is not applicable" />
          <Input size="small" disabled={!editable} placeholder="The reviewed rule (requirement id)" value={a.ruleRef}
            onChange={(e) => onChange(setApplicability(draft, c.id, { ...a, ruleRef: e.target.value }))} aria-label="Reviewed rule" />
        </div>
      )}
    </div>
  );
}

// ── Actors & timeline ───────────────────────────────────────────────────────

export function ActorsSection({ draft, onChange, editable, isDark, agentName }: SectionProps & { agentName: string }) {
  const peers = [...new Set([
    ...draft.checks.filter((c) => c.operator === 'tool.called' && c.args.tool?.toLowerCase() === 'a2a_consultpeer').flatMap((c) => Object.values(c.args.withArgs ?? {})),
    ...draft.checks.filter((c) => c.operator === 'peer.consulted' && c.args.value?.trim()).map((c) => c.args.value!.trim()),
  ])];
  const speaker = draft.world.memberContactId > 0 ? `Family contact #${draft.world.memberContactId}, signed in for the patient` : 'The patient, signed in as themself';
  const events = [...draft.events].sort((a, b) => a.turnIndex - b.turnIndex);

  const confirmRemove = (turn: number) => {
    const held = checksAtTurn(draft, turn);
    const { controls, doubles } = heldAtTurn(draft, turn);
    if (held.length + controls.length + doubles.length === 0) return onChange(removeTurn(draft, turn));
    const also = [controls.length && `${controls.length} control(s)`, doubles.length && `${doubles.length} tool answer(s)`].filter(Boolean).join(' and ');
    Modal.confirm({
      title: `Remove turn ${turn + 1} and its ${held.length} check(s)${also ? `, ${also}` : ''}?`,
      content: [...held.map((c) => checkSentence(c)), ...doubles.map((d) => `${d.tool} answers on this turn`), ...controls.map((c) => `Control ${c.id}`)].join(' · '),
      okText: 'Remove the turn',
      okButtonProps: { danger: true },
      onOk: () => onChange(removeTurn(draft, turn)),
    });
  };

  return (
    <section id="section-actors" aria-label="Actors and timeline">
      <Text style={sectionTitle}>Actors &amp; timeline</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Who takes part and what happens, in order. Each turn is fixed text, so every run of this version hears the same words.</Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginBottom: 14 }}>
        <div style={{ ...cardStyle(isDark), padding: 10 }}><Text strong>The user</Text><div style={{ fontSize: 12.5 }}>{speaker}</div></div>
        <div style={{ ...cardStyle(isDark), padding: 10 }}><Text strong>{agentName}</Text><div style={{ fontSize: 12.5 }}>The agent under test, on its own configured model</div></div>
        {peers.map((p) => (
          <div key={p} style={{ ...cardStyle(isDark), padding: 10 }}><Text strong>{p}</Text><div style={{ fontSize: 12.5 }}>Peer, on its configured model</div></div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {events.map((e) => {
          const held = checksAtTurn(draft, e.turnIndex);
          return (
            <div key={e.id} style={{ ...cardStyle(isDark), borderLeft: `3px solid ${colors.violet[500]}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Text strong>Turn {e.turnIndex + 1} · the user says</Text>
                {editable && events.length > 1 && (
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }} onClick={() => confirmRemove(e.turnIndex)} aria-label={`Remove turn ${e.turnIndex + 1}`} />
                )}
              </div>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} disabled={!editable} value={e.text}
                onChange={(ev) => onChange(setTurnText(draft, e.turnIndex, ev.target.value))} aria-label={`What the user says on turn ${e.turnIndex + 1}`} />
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
                Observe: the reply, every tool requested and any peer consulted · {held.length} check(s) at this turn
              </Text>
            </div>
          );
        })}
        {editable && <Button icon={<PlusOutlined />} onClick={() => onChange(addTurn(draft))}>Add a turn</Button>}
        <Text type="secondary" style={{ fontSize: 12 }}>End: after the agent’s reply to turn {events.length}.</Text>
      </div>
    </section>
  );
}

// ── Expectations ────────────────────────────────────────────────────────────

interface Obligation { key: string; title: string; checks: ScenarioCheck[]; turn?: number; ruleIndex?: number }

function obligationsOf(s: EvaluationScenario): Obligation[] {
  const turns = [...new Set(s.events.map((e) => e.turnIndex))].sort((a, b) => a - b);
  const byTurn = turns.map((t) => ({ key: `turn-${t}`, title: `Turn ${t + 1}: what the agent must do`, turn: t, checks: s.checks.filter((c) => c.scope === 'Turn' && c.turnIndex === t) }));
  const rules = [...new Set(s.checks.filter((c) => c.scope === 'Conversation').map((c) => c.ruleIndex ?? 0))].sort((a, b) => a - b);
  const byRule = rules.map((r) => {
    const checks = s.checks.filter((c) => c.scope === 'Conversation' && c.ruleIndex === r);
    return { key: `rule-${r}`, title: `Whole conversation: ${checks[0]?.label ?? 'a rule'}`, ruleIndex: r, checks };
  });
  return [...byTurn, ...byRule];
}

export function ExpectationsSection({ draft, onChange, editable, isDark }: SectionProps) {
  const obligations = obligationsOf(draft);
  const drafts = draft.checks.filter((c) => c.requirementRef?.trim().toLowerCase().startsWith('draft:'));
  return (
    <section id="section-expectations" aria-label="Expectations">
      <Text style={sectionTitle}>Expectations</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        What the agent must do. Every card must be met (AND); inside a check, alternatives marked OR mean one is enough. No dose, threshold or timing is
        written here: those come from the reviewed requirement a check points to.
      </Text>
      <Field label="What this scenario tests">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} disabled={!editable} value={draft.purpose ?? ''}
          onChange={(e) => onChange({ ...draft, purpose: e.target.value })} aria-label="What this scenario tests" />
      </Field>
      {drafts.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title={`Clinical draft: ${drafts.length} check(s) rest on a requirement still in draft`}
          description="This version can run, but only as Exploratory, and its results never count toward release. Approving the version is blocked until the requirement itself is reviewed; approving later does not promote earlier results." />
      )}
      {obligations.map((o, i) => (
        <div key={o.key}>
          {i > 0 && <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: colors.violet[600], margin: '6px 0' }}>AND</div>}
          <ObligationCard o={o} draft={draft} onChange={onChange} editable={editable} isDark={isDark} />
        </div>
      ))}
      {editable && <AddCheck draft={draft} onChange={onChange} />}
    </section>
  );
}

function ObligationCard({ o, draft, onChange, editable, isDark }: { o: Obligation; draft: EvaluationScenario; onChange: (s: EvaluationScenario) => void; editable: boolean; isDark: boolean }) {
  return (
    <div style={cardStyle(isDark)} aria-label={o.title}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Text strong>{o.title}</Text>
        <StatusPill tone="amber">Pending SME review</StatusPill>
      </div>
      {o.checks.length === 0 && <Text type="secondary">No check looks at this turn.</Text>}
      {o.checks.map((c) => <CheckEditor key={c.id} c={c} draft={draft} onChange={onChange} editable={editable} isDark={isDark} />)}
    </div>
  );
}

function CheckEditor({ c, draft, onChange, editable, isDark }: { c: ScenarioCheck; draft: EvaluationScenario; onChange: (s: EvaluationScenario) => void; editable: boolean; isDark: boolean }) {
  const strength = strengthWords(c);
  const setArgs = (change: Parameters<typeof updateArgs>[2]) => onChange(updateArgs(draft, c.id, change));
  const draftRef = c.requirementRef?.trim().toLowerCase().startsWith('draft:');
  const phraseLabel = isAlternatives(c) ? 'Alternatives (OR: one is enough)' : c.operator.startsWith('skills.') ? 'Skills' : c.operator === 'tool.notCalled' ? 'Tools' : 'Phrases';
  const withArgs = Object.entries(c.args.withArgs ?? {}).map(([k, v]) => `${k} = ${v}`);
  return (
    <div id={`check-${c.id}`} style={{ borderTop: `1px dashed ${surface(isDark).line}`, padding: '10px 0', borderLeft: draftRef ? `3px solid ${colors.amber[500]}` : undefined, paddingLeft: draftRef ? 10 : 0 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 11.5 }}>{checkName(draft, c)}</Text>
        <span style={{ flex: 1, minWidth: 200, fontSize: 13.5 }}>{checkSentence(c)}</span>
        <StatusPill tone="slate">{KIND_WORDS[c.kind] ?? c.kind}</StatusPill>
        <StatusPill tone={strength.tone}>{strength.label}</StatusPill>
        {editable && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(removeCheck(draft, c.id))} aria-label={`Remove ${checkName(draft, c)}`} />}
      </div>
      {editable && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, marginTop: 8 }}>
          {['tool.called', 'tool.notCalledWith'].includes(c.operator) && (
            <>
              <Input size="small" prefix={<Text type="secondary" style={{ fontSize: 12 }}>Tool</Text>} value={c.args.tool} onChange={(e) => setArgs({ tool: e.target.value || undefined })} aria-label="Tool" />
              <Select size="small" mode="tags" placeholder="Arguments: name = value" value={withArgs} aria-label="Arguments"
                onChange={(v: string[]) => setArgs({ withArgs: v.length ? Object.fromEntries(v.map((x) => x.split('=').map((p) => p.trim())).filter(([k, val]) => k && val)) : undefined })} />
              <Select size="small" mode="tags" placeholder="Its answer mentions…" value={tags(c.args.responseContains)} aria-label="Answer mentions"
                onChange={(v: string[]) => setArgs({ responseContains: v.length ? v : undefined })} />
            </>
          )}
          {c.operator === 'claim.stated' && (
            <>
              <Input size="small" prefix={<Text type="secondary" style={{ fontSize: 12 }}>Figure</Text>} value={c.args.value} onChange={(e) => setArgs({ value: e.target.value || undefined })} aria-label="Figure" />
              <Select size="small" mode="tags" placeholder="Beside these words" value={tags(c.args.sitsWith)} onChange={(v: string[]) => setArgs({ sitsWith: v.length ? v : undefined })} aria-label="Beside" />
              <Select size="small" mode="tags" placeholder="Never beside these words" value={tags(c.args.neverWith)} onChange={(v: string[]) => setArgs({ neverWith: v.length ? v : undefined })} aria-label="Never beside" />
            </>
          )}
          {c.operator === 'conversation.numbersGrounded' && (
            <>
              <span><Switch size="small" checked={c.args.enabled !== false} onChange={(on) => setArgs({ enabled: on })} aria-label="Numbers checked" /> <Text style={{ fontSize: 12.5 }}>Check numbers against tool output</Text></span>
              <InputNumber size="small" min={0} step={0.1} placeholder="Tolerance" value={c.args.tolerance} onChange={(v) => setArgs({ tolerance: v ?? undefined })} aria-label="Tolerance" />
              <Select size="small" mode="tags" placeholder="Ignore numbers matching…" value={tags(c.args.ignorePatterns)} onChange={(v: string[]) => setArgs({ ignorePatterns: v.length ? v : undefined })} aria-label="Ignore patterns" />
            </>
          )}
          {c.operator.startsWith('peer.') && (
            <Input size="small" prefix={<Text type="secondary" style={{ fontSize: 12 }}>Peer</Text>} placeholder="The peer’s canonical name, e.g. triage-nurse-oncology"
              value={c.args.value} onChange={(e) => setArgs({ value: e.target.value })} aria-label="Peer" />
          )}
          {c.operator === 'manual.obligation' && (
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="What the reviewer judges, in words, e.g. Does not suggest waiting until morning."
              value={c.args.value} onChange={(e) => setArgs({ value: e.target.value })} aria-label="What the reviewer judges" style={{ gridColumn: '1 / -1' }} />
          )}
          {!['tool.called', 'tool.notCalledWith', 'claim.stated', 'conversation.numbersGrounded', 'reply.severityScreenComplete', 'peer.consulted', 'peer.notConsulted', 'manual.obligation'].includes(c.operator) && (
            <Select size="small" mode="tags" placeholder={phraseLabel} value={tags(c.args.values)} aria-label={phraseLabel}
              onChange={(v: string[]) => setArgs({ values: v })} style={{ gridColumn: '1 / -1' }} />
          )}
          {c.scope === 'Conversation' && (
            <Input size="small" prefix={<Text type="secondary" style={{ fontSize: 12 }}>Rule</Text>} value={c.label} aria-label="Rule name"
              onChange={(e) => onChange({ ...draft, checks: draft.checks.map((x) => (x.scope === 'Conversation' && x.ruleIndex === c.ruleIndex ? { ...x, label: e.target.value } : x)) })} />
          )}
          <Input size="small" prefix={<Text type="secondary" style={{ fontSize: 12 }}>Requirement</Text>} placeholder="Not linked yet" value={c.requirementRef}
            onChange={(e) => onChange(updateCheck(draft, c.id, { requirementRef: e.target.value || undefined }))} aria-label="Requirement" />
        </div>
      )}
      {!editable && c.requirementRef && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Requirement: {c.requirementRef}</Text>}
      {c.operator.startsWith('peer.') && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
          The name the agent types is matched to this agent’s own peers the way consultation matches it, so a display name or slug counts.
          A run of an agent with no peers fails this check rather than guessing.
        </Text>
      )}
      {c.strength === 'Manual' && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
          No run marks this passed. A result whose automatic checks all pass reads Awaiting review until a reviewer decides it.
        </Text>
      )}
      {draftRef && (
        <Text style={{ display: 'block', fontSize: 12, marginTop: 4, color: isDark ? '#FCD34D' : colors.amber[700] }}>
          Clinical draft: blocks approval. The requirement behind this check is pending SME review.
        </Text>
      )}
    </div>
  );
}

function AddCheck({ draft, onChange }: { draft: EvaluationScenario; onChange: (s: EvaluationScenario) => void }) {
  const [type, setType] = useState<string>();
  const [where, setWhere] = useState<string>('turn-0');
  const turns = [...new Set(draft.events.map((e) => e.turnIndex))].sort((a, b) => a - b);
  const rules = [...new Map(draft.checks.filter((c) => c.scope === 'Conversation').map((c) => [c.ruleIndex ?? 0, c.label ?? 'a rule'])).entries()];
  const chosen = CHECK_TYPES.find((t) => t.operator === type);
  const placeOptions = chosen?.scope === 'Conversation'
    ? [...rules.map(([r, l]) => ({ value: `rule-${r}`, label: `Rule: ${l}` })), { value: 'rule-new', label: 'A new rule' }]
    : turns.map((t) => ({ value: `turn-${t}`, label: `Turn ${t + 1}` }));
  const add = () => {
    if (!chosen) return;
    const [kind, at] = where.split('-');
    onChange(addCheck(draft, chosen, kind === 'turn' ? { turn: Number(at) } : at === 'new' ? {} : { ruleIndex: Number(at) }));
    setType(undefined);
  };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      <Select size="small" placeholder="Add a check…" value={type} onChange={(v) => { setType(v); setWhere(CHECK_TYPES.find((t) => t.operator === v)?.scope === 'Conversation' ? (rules[0] ? `rule-${rules[0][0]}` : 'rule-new') : 'turn-0'); }}
        options={CHECK_TYPES.map((t) => ({ value: t.operator, label: t.label }))} style={{ minWidth: 260 }} aria-label="Check type" />
      {chosen && <Select size="small" value={where} onChange={setWhere} options={placeOptions} style={{ minWidth: 160 }} aria-label="Where" />}
      <Button size="small" icon={<PlusOutlined />} disabled={!chosen} onClick={add}>Add</Button>
    </div>
  );
}

// ── Checks & evidence ───────────────────────────────────────────────────────

export function ChecksSection({ draft, check, isDark }: SectionProps) {
  const exact = draft.checks.filter((c) => c.strength === 'Exact').length;
  const weak = draft.checks.filter((c) => c.strength === 'Weak').length;
  const manual = draft.checks.filter((c) => c.strength === 'Manual').length;
  const na = new Set(check?.notApplicableCheckIds ?? []);
  const s = surface(isDark);
  return (
    <section id="section-checks" aria-label="Checks and evidence">
      <Text style={sectionTitle}>Checks &amp; evidence</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {exact} observable-fact · {weak} keyword-strength · {manual} a reviewer judges · 0 semantic. Each check says what evidence it reads and whether an isolated run collects it.
      </Text>
      {weak > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title={`${weak} check(s) are keyword-strength`}
          description="They test words in the reply, so they can pass a wrong answer that uses the right words, or fail a safe paraphrase. Strengthen them with an observable fact where one exists." />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {draft.checks.map((c) => {
          const strength = strengthWords(c);
          return (
            <div key={c.id} style={{ ...cardStyle(isDark), padding: 10 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <Text type="secondary" style={{ fontSize: 11.5 }}>{checkName(draft, c)}</Text>
                <span style={{ flex: 1, minWidth: 200, fontSize: 13 }}>{checkSentence(c)}</span>
                <StatusPill tone={strength.tone}>{strength.label}</StatusPill>
                {na.has(c.id) && <StatusPill tone="slate">Not applicable</StatusPill>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6, marginTop: 6, fontSize: 12.5, color: s.muted }}>
                <span><b>Kind</b> {KIND_WORDS[c.kind] ?? c.kind}</span>
                <span><b>Evidence</b> {checkEvidence(c)}{c.strength === 'Manual' ? ' · decided by a person' : ' · collected'}</span>
              </div>
              {c.operator.startsWith('tool.') && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  In an isolated run a tool that changes a record or sends something is answered by a double and reported as Not carried out:
                  the request is observed, delivery is not.
                </Text>
              )}
              {na.has(c.id) && c.applicability?.reason && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Not applicable: {c.applicability.reason} ({c.applicability.ruleRef})</Text>}
            </div>
          );
        })}
      </div>
      <Alert type="info" showIcon style={{ marginTop: 12 }} title="How a result is decided"
        description={`Deterministic checks run first, every time. No semantic check is in this version, so the Evaluator judges nothing here.${manual > 0 ? ' A result whose automatic checks all pass reads Awaiting review while an obligation a reviewer judges waits for one.' : ''} Controls prove the checks themselves: a safe reply they must pass and an unsafe one they must fail (see Controls).`} />
      {(draft.notes ?? []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={fieldLabel}>The author’s notes · carried, never executed</Text>
          {(draft.notes ?? []).map((n, i) => <div key={i} style={{ fontSize: 12.5, marginBottom: 4 }}>{n.text}</div>)}
        </div>
      )}
    </section>
  );
}

// ── Provenance ──────────────────────────────────────────────────────────────

export function ProvenanceSection({ draft, isDark, versions, decisions, currentNumber, dirty, historyError }: SectionProps & {
  versions: ScenarioVersionRef[]; decisions: ScenarioDecisionRef[]; currentNumber?: number; dirty: boolean; historyError?: string | null;
}) {
  const p = draft.provenance ?? {};
  return (
    <section id="section-provenance" aria-label="Provenance">
      <Text style={sectionTitle}>Provenance</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Where this scenario came from, and who wrote, changed and approved each version.</Text>
      <div style={{ ...cardStyle(isDark), marginBottom: 12 }}>
        <div><Text type="secondary">Origin </Text>{ORIGIN_WORDS[p.origin ?? ''] ?? 'Not recorded'}</div>
        {p.sourcePath && <div style={{ wordBreak: 'break-all' }}><Text type="secondary">First written as </Text><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{p.sourcePath}</span></div>}
        {p.sourceHash && <div><Text type="secondary">Source hash </Text><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{shortHash(p.sourceHash)}</span></div>}
        {p.compilerVersion && <div><Text type="secondary">Compiled by </Text>{p.compilerVersion}</div>}
        <div style={{ marginTop: 6 }}>
          <Text type="secondary">Sources </Text>
          {(draft.sources ?? []).length > 0
            ? `${draft.sources!.length} named under Hazard & sources.`
            : 'None named yet. Name one under Hazard & sources; until then every clinical expectation stays pending SME review.'}
        </div>
      </div>
      {historyError ? (
        <Alert type="warning" showIcon title="The version history could not be read" description={historyError} />
      ) : (
        <ReviewRecord versions={versions} decisions={decisions} currentNumber={currentNumber} isDark={isDark} unsaved={dirty} />
      )}
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
        Any Studio user may record an approval; the record keeps who decided, in what role, and who authored the content. Approval is bound to a content hash:
        any change to expected behaviour, applicability, evidence requirements or checks makes a new, unapproved version.
      </Text>
    </section>
  );
}
